import { z } from 'zod';
import { cloudLinkMachineSchema } from './account';

/**
 * The link from a Crowdex server to Crowdex Cloud (`/api/cloud-link`). A user signs in to
 * the cloud from the browser; this server then sends conversations to the cloud, lists
 * the ones that live there and relays their requests. The cloud keeps the user's connections.
 */

export const cloudLinkAccountSchema = z.object({ email: z.string(), name: z.string() });

export const cloudLinkStatusSchema = z.object({
  /** The cloud this server links to; null when it links to none (`CROWDEX_CLOUD_URL`). */
  url: z.string().nullable(),
  /** The cloud account of this user; null until they link it. */
  account: cloudLinkAccountSchema.nullable(),
  /** The name this computer links under, shown among the account's linked computers. */
  machineName: z.string().optional(),
});

/** `POST /api/cloud-link`: the page opens `url` in the browser to sign in to the cloud. */
export const cloudLinkStartSchema = z.object({ url: z.string() });

/**
 * `POST /api/cloud-link/authorize` on the cloud: the signed-in user approves a link. The
 * browser then goes to `redirect`, the linking server's callback with a one-time code.
 */
export const cloudLinkAuthorizeRequestSchema = z.object({
  challenge: z.string().regex(/^[A-Za-z0-9_-]{43}$/),
  /** The linking server's callback; only a loopback address (a desktop's own server). */
  callback: z.string().url().max(2048),
  state: z.string().regex(/^[A-Za-z0-9_-]{16,128}$/),
});

export const cloudLinkAuthorizeResultSchema = z.object({ redirect: z.string() });

/** `POST /api/cloud-link/token` on the cloud: the linking server redeems the code. */
export const cloudLinkTokenRequestSchema = z.object({
  code: z.string().trim().min(16).max(128),
  verifier: z.string().min(43).max(128),
  /** The linking computer, named in the cloud's Settings › Account › Linked computers. */
  machine: cloudLinkMachineSchema.optional(),
});

export const cloudLinkTokenSchema = z.object({
  token: z.string().min(1),
  user: cloudLinkAccountSchema.extend({ id: z.string() }),
});

export type CloudLinkAccount = z.infer<typeof cloudLinkAccountSchema>;
export type CloudLinkStatus = z.infer<typeof cloudLinkStatusSchema>;
export type CloudLinkToken = z.infer<typeof cloudLinkTokenSchema>;
